import { currencyFormatter } from '@/helpers/currencyFormatter';
import { Alert, Box, Button, Column, Modal, Row, Spinner, Table } from '@palmetto/palmetto-components';
import { useMemo } from 'react';

import { AdhocBatchPreviewPayee, AdhocBatchPreviewResult } from 'types';

export const AdhocBatchConfirmationModal = ({
  isOpen,
  preview,
  isLoading,
  sourceLabel,
  sourceDetail,
  targetLabel,
  targetDetail,
  sourceWillEmpty,
  title = 'Confirm — Add to Batch',
  confirmLabel = 'Confirm — Add to Batch',
  onConfirm,
  onCancel,
  isConfirming,
}: {
  isOpen: boolean;
  preview?: AdhocBatchPreviewResult;
  isLoading: boolean;
  /** Top of the FROM column, e.g. "Unbatched Transactions". Omit to hide the FROM side. */
  sourceLabel?: string;
  /** Sub-line under the source, e.g. "141 selected". */
  sourceDetail?: string;
  /** Top of the TO column, e.g. the batch date. */
  targetLabel: string;
  /** Sub-line under the target, e.g. "Org". */
  targetDetail?: string;
  /** Warn that this move looks like it drains the source batch. */
  sourceWillEmpty?: boolean;
  /** Modal heading; defaults to the add-to-batch wording. */
  title?: string;
  /** Commit button copy; defaults to the add-to-batch wording. */
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isConfirming: boolean;
}) => {
  const columns: Column[] = useMemo(
    () => [
      {
        dataKey: 'payeeExternalId',
        heading: 'Payee',
        render: (cell?: unknown) => <Box fontWeight="medium">{(cell as string) || '-'}</Box>,
      },
      {
        dataKey: 'transactionCount',
        heading: 'Transactions',
        render: (cell?: unknown) => <Box>{(cell as number) ?? 0}</Box>,
      },
      {
        dataKey: 'combinedTotal',
        heading: 'Total',
        render: (_cell?: unknown, row?: Row) => {
          const payee = row as AdhocBatchPreviewPayee | undefined;
          return (
            <Box childGap="2xs" textAlign="right">
              <Box fontWeight="medium">{currencyFormatter.format(payee?.combinedTotal ?? 0)}</Box>
              {payee && payee.existingAmount ? (
                <Box fontSize="sm" color="body-secondary">
                  {currencyFormatter.format(payee.existingAmount)} existing +{' '}
                  {currencyFormatter.format(payee.newAmount)} new
                </Box>
              ) : null}
            </Box>
          );
        },
      },
    ],
    [],
  );

  const summary = preview?.summary;

  // A payee can appear once per batchType (determineBatchType splits e.g. org + holdback), so
  // payeeId alone is not a unique row key — combine it with batchType.
  const rows = useMemo(
    () =>
      (preview?.payees ?? []).map((payee) => ({ ...payee, payeeBatchKey: `${payee.payeeId}-${payee.batchType}` })),
    [preview],
  );

  return (
    <Modal isOpen={isOpen} ariaLabelledBy="adhocBatchConfirmationModal" onDismiss={onCancel} maxWidth="5xl">
      <Modal.Header id="adhocBatchConfirmationModal" title={title} onDismiss={onCancel} />
      <Modal.Body background="secondary" childGap="lg">
        <Box direction="row" alignItems="center" childGap="md" background="primary" radius="md" padding="md">
          {sourceLabel ? (
            <>
              <Box flex="auto" childGap="2xs">
                <Box fontSize="xs" color="body-secondary">
                  FROM
                </Box>
                <Box fontWeight="bold">{sourceLabel}</Box>
                {sourceDetail ? (
                  <Box fontSize="sm" color="body-secondary">
                    {sourceDetail}
                  </Box>
                ) : null}
              </Box>
              <Box color="body-secondary" fontSize="lg" padding="0 md">
                →
              </Box>
            </>
          ) : null}
          <Box flex="auto" childGap="2xs">
            <Box fontSize="xs" color="body-secondary">
              TO
            </Box>
            <Box fontWeight="bold">{targetLabel}</Box>
            {targetDetail ? (
              <Box fontSize="sm" color="body-secondary">
                {targetDetail}
              </Box>
            ) : null}
          </Box>
        </Box>

        {sourceWillEmpty ? (
          <Alert
            variant="warning"
            hasIcon
            title="This moves every transaction out of the source batch"
            message="The source batch will be left empty and removed. Filters may hide transactions that would stay behind."
          />
        ) : null}

        {preview?.hasNegativePayeeBatch ? (
          <Alert
            variant="warning"
            hasIcon
            title="Some payees net a zero or negative total"
            message="Review the totals below. You can still confirm and create the batch."
          />
        ) : null}

        {isLoading ? (
          <Box alignItems="center" padding="lg">
            <Spinner size="lg" />
          </Box>
        ) : (
          <Box childGap="md">
            {rows.length ? (
              <Table rowKey="payeeBatchKey" columns={columns} rows={rows} />
            ) : (
              <Box textAlign="center" padding="lg" color="body-secondary">
                No payees to batch.
              </Box>
            )}
            {summary ? (
              <Box direction="row" justifyContent="space-between" childGap="sm" padding="sm md" fontWeight="bold">
                <Box>
                  {summary.transactionCount} transaction{summary.transactionCount === 1 ? '' : 's'} ·{' '}
                  {summary.payeeCount} payee{summary.payeeCount === 1 ? '' : 's'}
                </Box>
                <Box>{currencyFormatter.format(summary.totalAmount)}</Box>
              </Box>
            ) : null}
          </Box>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" tone="neutral" onClick={onCancel} isDisabled={isConfirming}>
          ← Back
        </Button>
        <Button
          variant="primary"
          onClick={onConfirm}
          isLoading={isConfirming}
          isDisabled={isLoading || isConfirming || !preview?.payees.length}
        >
          {confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
